import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    CheckCircle2,
    FileText,
    Loader2,
    PenLine,
    ShieldCheck,
    XCircle,
} from "lucide-react";
import {
    getPublicQuote,
    acceptPublicQuote,
    getPublicQuoteAttachmentData,
} from "../lib/api";

const fmtMoney = (n) =>
    `$${(Number(n) || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

/**
 * Public, unauth'd quote landing page hit by the customer via the
 * shareable `/q/{token}` link. Renders the snapshot (so later edits
 * by the contractor never alter what was actually signed) and
 * captures a typed name + drawn signature on Accept.
 */
export default function PublicQuotePage() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [share, setShare] = useState(null);
    const [accepting, setAccepting] = useState(false);
    const [signedName, setSignedName] = useState("");
    const [signedEmail, setSignedEmail] = useState("");
    const [imageDataUrls, setImageDataUrls] = useState({}); // {storage_path: dataUrl}
    // iter-110: lifted contract-form state — needed here so the Accept
    // handler can bundle all fields into one POST, and so a previously
    // accepted share hydrates the read-only receipt correctly.
    const [initials, setInitials] = useState("");
    const [purch, setPurch] = useState({ name: "", title: "", date: "" });
    const [witness, setWitness] = useState({ name: "", title: "", date: "" });
    const [contractorDate, setContractorDate] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await getPublicQuote(token);
                if (cancelled) return;
                setShare(data);
                setSignedName(data?.signed_name || "");
                setSignedEmail(data?.signed_email || "");
                // Hydrate the lifted contract state from persisted fields
                // so accepted quotes re-render exactly as the customer
                // filled them in.
                setInitials(data?.initials || "");
                setPurch({
                    name: data?.purchaser_name || "",
                    title: data?.purchaser_title || "",
                    date: data?.purchaser_date || "",
                });
                setWitness({
                    name: data?.witness_name || "",
                    title: data?.witness_title || "",
                    date: data?.witness_date || "",
                });
                setContractorDate(data?.contractor_date || "");
            } catch (err) {
                if (cancelled) return;
                setError(err?.response?.data?.detail || "Quote not found.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token]);

    // Lazy-fetch image attachments so the customer can see disclaimer
    // photos / cover images inline. PDFs get a download link instead —
    // bundling pdfjs into this public page would balloon the JS payload.
    const snap = share?.snapshot || {};
    const attachments = Array.isArray(snap.attachments) ? snap.attachments : [];
    useEffect(() => {
        let cancelled = false;
        (async () => {
            for (const a of attachments) {
                if (a.kind !== "image" || imageDataUrls[a.storage_path]) continue;
                try {
                    const { data_url } = await getPublicQuoteAttachmentData(
                        token,
                        a.storage_path,
                    );
                    if (cancelled) return;
                    setImageDataUrls((prev) => ({ ...prev, [a.storage_path]: data_url }));
                } catch (e) {
                    // Skip individual failures — the quote still works
                    // without inline image previews; user can tap "View" instead.
                    // eslint-disable-next-line no-console
                    console.warn("Public quote attachment fetch failed:", a.storage_path, e);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, attachments.length]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading quote…
            </div>
        );
    }
    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
                <div className="max-w-md text-center" data-testid="public-quote-error">
                    <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                    <h1 className="text-2xl font-bold text-slate-800 mb-2">
                        Can&apos;t open quote
                    </h1>
                    <p className="text-slate-600 text-sm">{error}</p>
                </div>
            </div>
        );
    }

    const accepted = share?.status === "accepted";

    return (
        <div className="min-h-screen bg-slate-100 py-6 px-3 sm:py-10 sm:px-6 text-slate-800">
            <div
                className="max-w-3xl mx-auto bg-white rounded-md shadow-lg overflow-hidden text-slate-800"
                data-testid="public-quote-page"
            >
                {/* Header banner */}
                <div className="bg-[#0a0a0a] text-white px-5 py-4 flex items-center gap-3">
                    <ShieldCheck className="w-5 h-5 text-[#00C853] shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">
                            Customer quote
                        </div>
                        <div className="text-sm font-bold truncate">
                            {snap?.company?.name || "Quote"}
                        </div>
                    </div>
                    {accepted ? (
                        <span
                            className="text-[11px] font-bold uppercase tracking-wider bg-[#00C853]/20 text-[#00E676] px-2 py-1 rounded-sm"
                            data-testid="public-quote-status-accepted"
                        >
                            Accepted
                        </span>
                    ) : (
                        <span
                            className="text-[11px] font-bold uppercase tracking-wider bg-slate-700 text-slate-200 px-2 py-1 rounded-sm"
                            data-testid="public-quote-status-pending"
                        >
                            Pending
                        </span>
                    )}
                </div>

                {/* Quote body */}
                <QuoteSnapshotView
                    snap={snap}
                    accepted={accepted}
                    initials={initials}
                    setInitials={setInitials}
                    purch={purch}
                    setPurch={setPurch}
                    witness={witness}
                    setWitness={setWitness}
                    contractorDate={contractorDate}
                    setContractorDate={setContractorDate}
                />

                {/* Attachments preview */}
                {attachments.length > 0 && (
                    <div className="px-5 sm:px-8 pb-6 space-y-3">
                        <h3 className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">
                            Additional pages
                        </h3>
                        {attachments.map((a) => (
                            <div
                                key={a.id}
                                className="border border-slate-200 rounded-sm overflow-hidden"
                                data-testid={`public-quote-attachment-${a.id}`}
                            >
                                {a.kind === "image" && imageDataUrls[a.storage_path] ? (
                                    <img
                                        src={imageDataUrls[a.storage_path]}
                                        alt={a.name}
                                        className="w-full max-h-[80vh] object-contain bg-slate-50"
                                    />
                                ) : a.kind === "image" ? (
                                    <div className="px-4 py-6 text-sm text-slate-500 text-center bg-slate-50">
                                        Loading {a.name}…
                                    </div>
                                ) : (
                                    <a
                                        href={`/api/public/quotes/${token}/attachment-data?storage_path=${encodeURIComponent(a.storage_path)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50"
                                        onClick={(e) => {
                                            // The endpoint returns JSON {data_url}; intercept and
                                            // open the data URL in a new tab so the PDF actually
                                            // displays instead of dumping JSON.
                                            e.preventDefault();
                                            getPublicQuoteAttachmentData(
                                                token,
                                                a.storage_path,
                                            )
                                                .then(({ data_url }) => {
                                                    const w = window.open();
                                                    if (w) w.location.href = data_url;
                                                })
                                                .catch(() =>
                                                    toast.error(
                                                        "Couldn't open attachment",
                                                    ),
                                                );
                                        }}
                                    >
                                        <FileText className="w-4 h-4 text-[#FF6B6B] shrink-0" />
                                        <span className="text-sm font-semibold text-slate-700 flex-1 truncate">
                                            {a.name}
                                        </span>
                                        <span className="text-[11px] text-slate-400">
                                            View PDF
                                        </span>
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Accept block */}
                {accepted ? (
                    <AcceptedReceipt share={share} />
                ) : (
                    <AcceptForm
                        accepting={accepting}
                        signedName={signedName}
                        setSignedName={setSignedName}
                        signedEmail={signedEmail}
                        setSignedEmail={setSignedEmail}
                        onAccept={async (signatureDataUrl) => {
                            if (!signedName.trim()) {
                                toast.error("Please type your name.");
                                return;
                            }
                            if (!signatureDataUrl) {
                                toast.error("Please draw your signature.");
                                return;
                            }
                            setAccepting(true);
                            try {
                                const updated = await acceptPublicQuote(token, {
                                    signed_name: signedName.trim(),
                                    signed_email: signedEmail.trim(),
                                    signature_data_url: signatureDataUrl,
                                    // iter-110: persist Terms & Signature
                                    // page fields alongside the drawn sig.
                                    initials: initials.trim(),
                                    purchaser_name: purch.name.trim(),
                                    purchaser_title: purch.title.trim(),
                                    purchaser_date: purch.date.trim(),
                                    witness_name: witness.name.trim(),
                                    witness_title: witness.title.trim(),
                                    witness_date: witness.date.trim(),
                                    contractor_date: contractorDate.trim(),
                                });
                                setShare(updated);
                                toast.success("Quote accepted — thank you!");
                            } catch (err) {
                                toast.error(
                                    err?.response?.data?.detail
                                        || "Couldn't accept quote",
                                );
                            } finally {
                                setAccepting(false);
                            }
                        }}
                    />
                )}
            </div>
        </div>
    );
}

function QuoteSnapshotView({
    snap,
    accepted,
    initials,
    setInitials,
    purch,
    setPurch,
    witness,
    setWitness,
    contractorDate,
    setContractorDate,
}) {
    const company = snap?.company || {};
    const customer = snap?.customer || {};
    const lines = Array.isArray(snap?.lines) ? snap.lines : [];
    const addressLines = (company.address_lines || []).filter(Boolean);
    return (
        <div className="px-5 sm:px-8 py-6">
            <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_auto] gap-4 mb-5 pb-3 border-b border-slate-200">
                <div className="w-[140px] h-[90px] flex items-center">
                    {company.logo_data_url ? (
                        <img
                            src={company.logo_data_url}
                            alt="Logo"
                            className="max-w-[140px] max-h-[90px] object-contain"
                        />
                    ) : null}
                </div>
                <div className="text-[13px] leading-tight text-slate-800">
                    <div className="font-bold text-[15px] mb-0.5">
                        {company.name || ""}
                    </div>
                    {addressLines.map((l, i) => (
                        <div key={i}>{l}</div>
                    ))}
                    {company.phone && <div>{company.phone}</div>}
                    {company.prepared_by && (
                        <div>Prepared by: {company.prepared_by}</div>
                    )}
                    {company.email && <div>{company.email}</div>}
                </div>
                <div className="text-[11px] space-y-1 md:justify-self-end">
                    <MetaRow label="Date" value={snap.date_str || ""} />
                    <MetaRow
                        label="Valid Until"
                        value={company.valid_days || ""}
                    />
                    <MetaRow
                        label="Quote ID"
                        value={snap.quote_id || "—"}
                        mono
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5">
                <div>
                    <div className="bg-[#1a3fd1] text-white font-bold px-3 py-1.5 uppercase tracking-wide text-xs mb-2">
                        Customer
                    </div>
                    <div className="text-sm text-slate-800">
                        {customer.name && (
                            <div className="font-bold">{customer.name}</div>
                        )}
                        {customer.address && <div>{customer.address}</div>}
                        {customer.city && <div>{customer.city}</div>}
                    </div>
                </div>
                <div className="text-sm text-slate-800 self-end">
                    {customer.phone && <div>{customer.phone}</div>}
                    {customer.email && <div>{customer.email}</div>}
                </div>
            </div>

            <table className="w-full text-sm border-collapse">
                <thead>
                    <tr className="border-b-2 border-slate-900 text-xs uppercase tracking-wide text-slate-600">
                        <th className="text-left px-3 py-2.5 w-[50px]">Item</th>
                        <th className="text-left px-3 py-2.5">Description</th>
                        <th className="text-right px-3 py-2.5 w-[140px]">
                            Amount
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {lines.length === 0 ? (
                        <tr>
                            <td
                                colSpan={3}
                                className="text-center text-slate-500 py-6"
                            >
                                No line items.
                            </td>
                        </tr>
                    ) : (
                        lines.map((ln, i) => (
                            <tr
                                key={i}
                                className="border-b border-slate-200 align-top"
                            >
                                <td className="px-3 py-3 text-slate-500">
                                    {i + 1}
                                </td>
                                <td className="px-3 py-3 text-slate-800">
                                    {ln.name || ""}
                                    {ln.notes && (
                                        <div className="text-xs text-slate-500 mt-0.5">
                                            {ln.notes}
                                        </div>
                                    )}
                                </td>
                                <td className="px-3 py-3 text-right tabular-nums font-semibold text-slate-900">
                                    {fmtMoney(ln.amount)}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            <div className="mt-2 text-sm">
                <Row
                    label="Subtotal"
                    value={fmtMoney(snap.subtotal)}
                    testid="public-quote-subtotal"
                />
                <Row
                    label={`Tax${snap.tax_label ? ` · ${snap.tax_label}` : ""}`}
                    value={fmtMoney(snap.tax_amount)}
                    testid="public-quote-tax"
                />
                <Row
                    label="Grand Total"
                    value={fmtMoney(snap.grand_total)}
                    grand
                    testid="public-quote-grand"
                />
            </div>

            {/* iter-110: Steeltooth terms/payment/signature pages with
                fillable initials/name/title/date fields. Client-side
                only — makes the sign-share view match the paper quote
                the customer is used to seeing. */}
            <QuoteTermsPages
                snap={snap}
                company={company}
                accepted={accepted}
                initials={initials}
                setInitials={setInitials}
                purch={purch}
                setPurch={setPurch}
                witness={witness}
                setWitness={setWitness}
                contractorDate={contractorDate}
                setContractorDate={setContractorDate}
            />
        </div>
    );
}

function QuoteTermsPages({
    snap,
    company,
    accepted = false,
    initials,
    setInitials,
    purch,
    setPurch,
    witness,
    setWitness,
    contractorDate,
    setContractorDate,
}) {
    const subtotal = Number(snap?.subtotal || 0);
    const taxAmount = Number(snap?.tax_amount || 0);
    const grandTotal = Number(snap?.grand_total || 0);
    const depositBase = subtotal / 2;
    const finalBase = subtotal / 2;
    const page2Items = [
        "Work is to be continuous and completed in ONE mobilization(s) unless otherwise noted in scope: Additional mobilization is $1,850.00. Any delays or work stoppages in the work described above, caused by no fault of STEELTOOTH CONTRACTING, will be back charged at a rate of: $500.00/crew hour (partial hour delays will be accumulated daily). All payments must be paid in full to validate warranty.",
        "All quantities are estimates only, final invoice will be based on actual measured quantities.",
        "Under no circumstances shall anyone other than our firm be entitled to use our construction equipment or rentals.",
        "Prices valid for the 2026 construction season and open to acceptance for 15 days from the Bid Date, subject to the notes below.",
        "STEELTOOTH CONTRACTING is not responsible for any rock/cobble/shale excavation, additional costs will apply.",
        "Any/all material to be hauled off site/testing/loading/hauling and disposal fees are extra unless otherwise identified.",
        "All excavated material to meet Table 1, any exceedance will be charged extra.",
        "All haulage to be completed prior to March 1st or after April 30th if designated a load restricted road otherwise additional charges will apply.",
        "No allowances have been made for shoring/hording/fencing/ protection of excavation and disposal, to be completed on T&M.",
        "Any work within 1m of underground utilities must be completed using hydrovac excavation and will be billed EXTRA in accordance to added scope.",
        "All material testing and compaction testing (QA/QC) by others.",
        "Dust control/ street sweeping done by others.",
        "Field office, portable toilets and garbage bins provided by others.",
        "Clear and unobstructed access to the site is to be provided by the owner during normal working hours of 7am-4pm M-F.",
        "Traffic control/ traffic protection/flagging/signage/barriers by others.",
        "All fees/ permits, road closures, road housekeeping, air rights, etc., by others.",
        "Private locates and Pre-condition survey by others.",
        "Bonding not included. Wrap-up/ builder risk liability insurance not included.",
        "Payment terms: 50% Deposit, 50% due upon completion.",
        "Any work not shown as a scope, drawings or agreed upon before commencing of work.",
        "STEELTOOTH CONTRACTING will not be responsible for delays in completion of work arising from strikes, walkouts, lockouts, material shortages, equipment breakdowns, fires, any acts of God or any other causes beyond the control of STEELTOOTH CONTRACTING.",
    ];
    const page3Items = [
        "Any winter housekeeping such as: cleaning snow, frost protection, heating footings, breaking frost, defrost ice or dirt etc, unless listed above on scope of work.",
        "STEELTOOTH CONTRACTING will not be responsible for damage caused by mobilization of heavy equipment to complete scope of work.",
        "No Asbestos, Lead, Mercury or other hazardous substances abatement.",
        "Electrical wiring to be done by others.",
        "Any dewatering required is not included in this scope of work, can be provided on a time and material basis unless otherwise specified.",
        "All funds in Canadian dollars and applicable taxes are rated at 13%.",
        "All material remains the property of STEELTOOTH CONTRACTING until final invoice has been paid.",
        "No allowances have been made to secure tanks to the ground in the event of excessive groundwater and upward pressure.",
        "Tree removal by others, stump disposal extra unless otherwise noted.",
        "Final grading, additional sod/seed and additional topsoil is not included and extra.",
        "Plumber is responsible to connect and seal sewer pipe from house into the septic tank as per plumbing regulations.",
        "Annual maintenance contract must be signed with tertiary unit manufacturer.",
        "Anything additional requested by the Inspection Authority or Township, not included in the drawings is billed at a time and material basis.",
    ];
    const brand = (
        <div className="flex items-center gap-3 pb-2 mb-4 border-b-2 border-[#1a3fd1]/50">
            {company?.logo_data_url ? (
                <img
                    src={company.logo_data_url}
                    alt="Logo"
                    className="max-w-[70px] max-h-[54px] object-contain"
                />
            ) : null}
            <div>
                <div className="text-[#8fa4f0] font-extrabold tracking-widest text-lg leading-none">
                    STEELTOOTH
                </div>
                <div className="text-slate-500 text-[10px] tracking-[0.24em] mt-0.5">
                    — CONTRACTING —
                </div>
            </div>
        </div>
    );
    const initialsRow = (value, onChange, testidBase) => (
        <div className="flex items-center gap-3 mt-6 pt-3 border-t border-dashed border-slate-300">
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                Initial Here:
            </span>
            <input
                type="text"
                maxLength={4}
                value={value}
                onChange={(e) => onChange(e.target.value.toUpperCase())}
                disabled={accepted}
                data-testid={`${testidBase}-a`}
                aria-label="Initials"
                className="w-11 h-11 rounded-full border-2 border-[#1a3fd1]/50 text-center font-bold text-[#8fa4f0] uppercase text-sm outline-none focus:bg-[#1a3fd1]/5 disabled:bg-slate-100 disabled:cursor-not-allowed"
            />
            <input
                type="text"
                maxLength={4}
                value={value}
                onChange={(e) => onChange(e.target.value.toUpperCase())}
                disabled={accepted}
                data-testid={`${testidBase}-b`}
                aria-label="Initials (duplicate)"
                className="w-11 h-11 rounded-full border-2 border-[#1a3fd1]/50 text-center font-bold text-[#8fa4f0] uppercase text-sm outline-none focus:bg-[#1a3fd1]/5 disabled:bg-slate-100 disabled:cursor-not-allowed"
            />
        </div>
    );
    const sigBlock = (role, state, setState, testidBase, roleFixed = false) => (
        <div
            className="grid grid-cols-[40px_1fr] gap-3 mt-5"
            data-testid={`${testidBase}-block`}
        >
            <div className="text-3xl font-extrabold text-[#8fa4f0] leading-none">X</div>
            <div>
                <div className="font-bold text-slate-900 text-sm mb-1">{role}</div>
                {!roleFixed && (
                    <>
                        <SigLine
                            label="Name"
                            value={state.name}
                            onChange={(v) => setState({ ...state, name: v })}
                            testid={`${testidBase}-name`}
                            disabled={accepted}
                        />
                        <SigLine
                            label="Title"
                            value={state.title}
                            onChange={(v) => setState({ ...state, title: v })}
                            testid={`${testidBase}-title`}
                            disabled={accepted}
                        />
                    </>
                )}
                <SigLine
                    label="Date"
                    value={state.date}
                    onChange={(v) => setState({ ...state, date: v })}
                    testid={`${testidBase}-date`}
                    type="date"
                    disabled={accepted}
                />
                <div className="text-[11px] italic text-slate-500 mt-1">
                    (I have authority to bind the Company)
                </div>
            </div>
        </div>
    );
    return (
        <>
            <PageBreak />
            <section
                className="px-5 sm:px-8 py-6"
                data-testid="public-quote-terms-page-2"
            >
                {brand}
                <h2 className="text-center text-[#8fa4f0] font-extrabold tracking-widest text-base mb-3">
                    TERMS AND CONDITIONS
                </h2>
                <ul className="text-[13px] leading-relaxed space-y-1 list-none pl-0">
                    {page2Items.map((t, i) => (
                        <li key={i} className="pl-3 relative">
                            <span className="absolute left-0 text-[#8fa4f0] font-bold">–</span>
                            {t}
                        </li>
                    ))}
                </ul>
                {initialsRow(initials, setInitials, "public-quote-initials-p2")}
                <div className="text-xs text-slate-500 mt-4">Page 2 of 4</div>
            </section>

            <PageBreak />
            <section
                className="px-5 sm:px-8 py-6"
                data-testid="public-quote-terms-page-3"
            >
                {brand}
                <h2 className="text-center text-[#8fa4f0] font-extrabold tracking-widest text-base mb-3">
                    TERMS AND CONDITIONS
                </h2>
                <ul className="text-[13px] leading-relaxed space-y-1 list-none pl-0">
                    {page3Items.map((t, i) => (
                        <li key={i} className="pl-3 relative">
                            <span className="absolute left-0 text-[#8fa4f0] font-bold">–</span>
                            {t}
                        </li>
                    ))}
                </ul>
                <p className="text-center italic font-bold text-[#8fa4f0] mt-4 text-sm">
                    *** Client agrees to pay 2836478 ONTARIO INC the below amounts in schedule ***
                </p>
                <div
                    className="border-2 border-[#1a3fd1]/50 p-4 my-3"
                    data-testid="public-quote-payment-box"
                >
                    <div className="text-[#8fa4f0] font-extrabold tracking-widest text-base">
                        PAYMENT
                    </div>
                    <div className="text-slate-500 font-semibold text-xs tracking-widest mb-2">
                        SCHEDULE A
                    </div>
                    <PayRow
                        label="DEPOSIT"
                        value={
                            <>
                                {fmtMoney(depositBase)}{" "}
                                <em className="not-italic text-slate-500 text-xs ml-1">+TAX</em>
                            </>
                        }
                        testid="public-quote-deposit"
                    />
                    <PayRow
                        label="FINAL"
                        value={
                            <>
                                {fmtMoney(finalBase)}{" "}
                                <em className="not-italic text-slate-500 text-xs ml-1">+TAX</em>
                            </>
                        }
                        testid="public-quote-final"
                    />
                    <PayRow
                        label="TOTAL"
                        value={fmtMoney(subtotal)}
                        testid="public-quote-payment-total"
                    />
                    <PayRow
                        label="TAX"
                        value={fmtMoney(taxAmount)}
                        testid="public-quote-payment-tax"
                    />
                    <PayRow
                        label="Total Amount:"
                        value={fmtMoney(grandTotal)}
                        grand
                        testid="public-quote-payment-grand"
                    />
                </div>
                <p className="text-[11px] italic text-slate-600 leading-relaxed">
                    Invoiced prior to work completed and materials supplied.
                    Payment upon completion of work and/or materials. No Holdback.
                    An interest charge of 2% per month (24% annually) will be assessed
                    to overdue accounts. Nonpayment could be considered grounds for us
                    to stop work, delay project and/or termination of contract.
                </p>
                {initialsRow(initials, setInitials, "public-quote-initials-p3")}
                <div className="text-xs text-slate-500 mt-4">Page 3 of 4</div>
            </section>

            <PageBreak />
            <section
                className="px-5 sm:px-8 py-6"
                data-testid="public-quote-terms-page-4"
            >
                {brand}
                {sigBlock("Purchaser", purch, setPurch, "public-quote-purchaser")}
                {sigBlock("Witness", witness, setWitness, "public-quote-witness")}
                {sigBlock(
                    "2836478 ONTARIO INC · Corey Watman — President",
                    { name: "", title: "", date: contractorDate },
                    (s) => setContractorDate(s.date),
                    "public-quote-contractor",
                    true,
                )}
                <p className="text-[11px] italic text-slate-600 mt-6 leading-relaxed">
                    This quotation becomes effective as a contract when signed by
                    duly authorized representative of each party. Any changes to
                    this quotation must, however, be signed by a representative
                    from 2836478 ONTARIO INC. Instructions in writing or verbally
                    will serve as an irrevocable agreement from Owner or General
                    Contractor, to proceed with the contract work.
                </p>
                <p className="text-[11px] italic text-slate-600 mt-2 leading-relaxed">
                    Please sign and return to email:{" "}
                    <strong className="text-slate-800">INFO@STEELTOOTH.CA</strong>{" "}
                    Or Call <strong className="text-slate-800">705-333-4376</strong>
                </p>
                <p className="text-center font-extrabold text-[#8fa4f0] mt-4 text-sm">
                    &ldquo;This quote expires in 30 days from the above listed date.&rdquo;
                </p>
                <div className="text-xs text-slate-500 mt-4">Page 4 of 4</div>
            </section>
        </>
    );
}

function PageBreak() {
    return (
        <div
            className="my-6 mx-5 sm:mx-8 border-t-2 border-dashed border-slate-200"
            aria-hidden="true"
        />
    );
}

function SigLine({ label, value, onChange, testid, type = "text", disabled = false }) {
    return (
        <div className="flex items-baseline gap-2 py-1">
            <span className="text-xs font-bold text-slate-700 min-w-[46px]">
                {label}:
            </span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                data-testid={testid}
                className="flex-1 border-b border-[#1a3fd1]/50 bg-transparent outline-none py-0.5 text-sm text-slate-900 focus:border-[#1a3fd1]/50 disabled:text-slate-500 disabled:cursor-not-allowed"
            />
        </div>
    );
}

function PayRow({ label, value, grand, testid }) {
    return (
        <div
            className={`flex justify-between py-1 ${grand ? "border-t-2 border-[#1a3fd1]/50 mt-2 pt-2 font-extrabold text-[#8fa4f0]" : "border-b border-dashed border-slate-200"}`}
            data-testid={testid}
        >
            <span className="text-sm text-slate-800">{label}</span>
            <span className="text-sm tabular-nums">{value}</span>
        </div>
    );
}

function Row({ label, value, grand, testid }) {
    return (
        <div
            className={`flex justify-between py-2.5 px-3 ${grand ? "bg-slate-100 font-extrabold text-base text-slate-900" : "border-b border-slate-200 text-slate-700"}`}
            data-testid={testid}
        >
            <span className={`uppercase tracking-wide ${grand ? "" : "text-xs font-bold"}`}>
                {label}
            </span>
            <span className="tabular-nums">{value}</span>
        </div>
    );
}

function MetaRow({ label, value, mono = false }) {
    return (
        <div className="flex justify-end items-center gap-2 text-slate-700">
            <span className="font-bold uppercase tracking-wide text-xs">
                {label}
            </span>
            <span
                className={`inline-block border border-slate-900 px-2 py-0.5 min-w-[92px] text-center bg-slate-50 text-slate-900 ${mono ? "font-mono" : ""}`}
            >
                {value}
            </span>
        </div>
    );
}

function AcceptForm({
    accepting,
    signedName,
    setSignedName,
    signedEmail,
    setSignedEmail,
    onAccept,
}) {
    const canvasRef = useRef(null);
    const [hasInk, setHasInk] = useState(false);
    const drawing = useRef(false);
    const lastPoint = useRef(null);

    // Resize canvas to its CSS size with proper devicePixelRatio scaling.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ratio = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * ratio;
        canvas.height = rect.height * ratio;
        const ctx = canvas.getContext("2d");
        ctx.scale(ratio, ratio);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#0F172A";
    }, []);

    const pointFromEvent = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const t = e.touches?.[0] || e.changedTouches?.[0] || e;
        return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    };

    const start = (e) => {
        e.preventDefault();
        drawing.current = true;
        lastPoint.current = pointFromEvent(e);
    };
    const move = (e) => {
        if (!drawing.current) return;
        e.preventDefault();
        const p = pointFromEvent(e);
        const ctx = canvasRef.current.getContext("2d");
        ctx.beginPath();
        ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        lastPoint.current = p;
        if (!hasInk) setHasInk(true);
    };
    const end = (e) => {
        if (!drawing.current) return;
        e.preventDefault();
        drawing.current = false;
        lastPoint.current = null;
    };

    const clear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
        setHasInk(false);
    };

    const submit = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dataUrl = canvas.toDataURL("image/png");
        onAccept(dataUrl);
    };

    return (
        <div className="border-t border-slate-200 px-5 sm:px-8 py-6 bg-slate-50">
            <h3 className="text-sm font-bold text-slate-800 mb-1 flex items-center gap-2">
                <PenLine className="w-4 h-4 text-[#8fa4f0]" />
                Accept this quote
            </h3>
            <p className="text-xs text-slate-500 mb-4">
                Type your name and sign below to accept the totals shown above.
                Your signature, IP, and timestamp are recorded for record-keeping.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                        Your full name
                    </label>
                    <input
                        type="text"
                        value={signedName}
                        onChange={(e) => setSignedName(e.target.value)}
                        placeholder="Jane Customer"
                        data-testid="public-quote-name"
                        className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-[#1a3fd1]/50 outline-none"
                    />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                        Email (optional)
                    </label>
                    <input
                        type="email"
                        value={signedEmail}
                        onChange={(e) => setSignedEmail(e.target.value)}
                        placeholder="you@example.com"
                        data-testid="public-quote-email"
                        className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-[#1a3fd1]/50 outline-none"
                    />
                </div>
            </div>
            <div>
                <div className="flex items-center justify-between mb-1">
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                        Signature
                    </label>
                    <button
                        type="button"
                        onClick={clear}
                        data-testid="public-quote-signature-clear"
                        className="text-[11px] text-slate-500 hover:text-slate-700 underline"
                    >
                        Clear
                    </button>
                </div>
                <canvas
                    ref={canvasRef}
                    onMouseDown={start}
                    onMouseMove={move}
                    onMouseUp={end}
                    onMouseLeave={end}
                    onTouchStart={start}
                    onTouchMove={move}
                    onTouchEnd={end}
                    data-testid="public-quote-signature"
                    className="w-full h-40 bg-white border-2 border-dashed border-slate-300 rounded-sm touch-none"
                />
            </div>
            <button
                type="button"
                onClick={submit}
                disabled={accepting || !signedName.trim() || !hasInk}
                data-testid="public-quote-accept-btn"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm bg-[#00C853] hover:bg-[#00E676] disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold text-sm uppercase tracking-wider transition-colors"
            >
                {accepting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    <CheckCircle2 className="w-4 h-4" />
                )}
                {accepting ? "Submitting…" : "Accept & sign"}
            </button>
        </div>
    );
}

function AcceptedReceipt({ share }) {
    const acceptedAt = useMemo(() => {
        try {
            return new Date(share.accepted_at).toLocaleString();
        } catch {
            return share.accepted_at || "";
        }
    }, [share.accepted_at]);
    // iter-110: contract fields captured on Terms & Signature pages.
    const contract = [
        ["Initials", share.initials],
        ["Purchaser name", share.purchaser_name],
        ["Purchaser title", share.purchaser_title],
        ["Purchaser date", share.purchaser_date],
        ["Witness name", share.witness_name],
        ["Witness title", share.witness_title],
        ["Witness date", share.witness_date],
        ["Contractor date", share.contractor_date],
    ].filter(([, v]) => (v || "").toString().trim());
    return (
        <div
            className="border-t border-slate-200 px-5 sm:px-8 py-6 bg-[#00C853]/10"
            data-testid="public-quote-accepted-block"
        >
            <h3 className="text-base font-bold text-slate-800 mb-2 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#00C853]" />
                Quote accepted — thank you!
            </h3>
            <p className="text-sm text-slate-600 mb-4">
                Signed by <strong>{share.signed_name}</strong>
                {share.signed_email ? ` · ${share.signed_email}` : ""}
                {" "}
                on {acceptedAt}.
            </p>
            {share.signature_data_url && (
                <div className="bg-white border border-slate-300 rounded-sm p-3 inline-block">
                    <img
                        src={share.signature_data_url}
                        alt="Signature"
                        className="max-w-[280px] max-h-24"
                    />
                </div>
            )}
            {contract.length > 0 && (
                <div
                    className="mt-4 bg-white border border-slate-200 rounded-sm p-4"
                    data-testid="public-quote-accepted-contract-fields"
                >
                    <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-2">
                        Signed contract fields
                    </div>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                        {contract.map(([label, value]) => (
                            <div key={label} className="flex gap-2">
                                <dt className="font-bold text-slate-700 min-w-[130px]">
                                    {label}:
                                </dt>
                                <dd className="text-slate-900 break-words">{value}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
            )}
        </div>
    );
}
